import { SocialPlatform } from '@mg/shared';
import { useTenant } from '../auth/TenantContext';
import { industryPackLabel, enumLabel } from '../lib/display';
import { platformName } from '../features/social/platforms';
import { PageHeader } from '../components/shell/PageHeader';
import { menuIcons } from '../components/shell/menuIcons';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/States';
import { MembersCard } from '../features/members/MembersCard';

/** A workspace's platforms are stored as strings, each a SocialPlatform as the seed writes it. */
const isPlatform = (value: string): value is SocialPlatform =>
  (Object.values(SocialPlatform) as string[]).includes(value);

export function SettingsPage() {
  const { activeTenant } = useTenant();

  return (
    <>
      {/* No menu links to this page, so there is no entry to copy a glyph from: it takes App Settings' gear. */}
      <PageHeader
        title="Settings"
        icon={menuIcons.appSettings}
        description="Brand Workspace configuration, compliance, and members."
      />

      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Brand Workspace"
            description="Identity and routing for the active workspace."
          />
          <CardBody>
            {activeTenant ? (
              <dl className="grid gap-4 sm:grid-cols-2">
                <Detail label="Name" value={activeTenant.name} />
                <Detail label="Slug" value={activeTenant.slug} mono />
                <Detail
                  label="Compliance pack"
                  value={industryPackLabel[activeTenant.industryPack]}
                />
                <Detail label="Demo workspace" value={activeTenant.isDemo ? 'Yes' : 'No'} />
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">
                    Confirmed platforms
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-1.5">
                    {activeTenant.platforms.length > 0 ? (
                      activeTenant.platforms.map((p) => (
                        <Badge key={p} tone="brand">
                          {isPlatform(p) ? platformName(p) : enumLabel(p)}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-ink-faint">None configured</span>
                    )}
                  </dd>
                </div>
              </dl>
            ) : (
              <EmptyState title="No active Brand Workspace" />
            )}
          </CardBody>
        </Card>

        <MembersCard />
      </div>
    </>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className={mono ? 'mt-1 font-mono text-sm text-ink-strong' : 'mt-1 text-sm text-ink-strong'}>
        {value}
      </dd>
    </div>
  );
}
